/* ==============================================
   THE PAINTED WOLVES — accessibility pass 2, DOM side (v13, 19 Sep 2026)

   Loaded with defer from sections/pw-peek.liquid, which sits in the footer
   group and therefore renders on every page.

   Two fixes:

   1. DANGLING ARIA REFERENCES (axe aria-valid-attr-value, critical)
      - input#Search-In-Modal[role=combobox] ships aria-controls and
        aria-owns pointing at "predictive-search-results", which is only
        rendered once the visitor types and predictive search is off.
      - Product card links ship aria-labelledby token lists that include
        Badge-<section>-<id> and NoMediaStandardBadge-<section>-<id>, which
        only render for products that carry a badge. 16 dangling refs on the
        home page at time of writing.
      Every element carrying aria-controls, aria-owns, aria-labelledby or
      aria-describedby has its token list walked; tokens that do not resolve
      with getElementById are dropped and the attribute is removed outright
      if nothing survives. The original value is stashed on a data attribute
      and restored token by token if the target ever shows up, so turning
      predictive search back on does not leave it un-wired.

   2. MOUSE-ONLY PRODUCT ZOOM (WCAG 2.1.1 Level A)
      img[data-pw-zoom] opens .pw-lb on click but had no tabindex, no role
      and no key handler, so the lightbox could not be opened from the
      keyboard at all. Each image becomes a real button, Enter and Space
      dispatch a click so the existing handler stays the single source of
      truth, focus moves into the lightbox on open, Tab is trapped inside
      it, Escape closes and focus returns to the image that opened it.

   Everything is wrapped so a throw here can never take the page with it.
   ============================================== */

(function () {
  'use strict';

  var ARIA_REF_ATTRS = ['aria-controls', 'aria-owns', 'aria-labelledby', 'aria-describedby'];
  var STASH_PREFIX = 'data-pw-aria-';
  var ZOOM_SELECTOR = 'img[data-pw-zoom]';
  var LIGHTBOX_SELECTOR = '[data-pw-lightbox], .pw-lb';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastOpener = null;

  function each(list, fn) {
    if (!list) return;
    for (var i = 0; i < list.length; i++) {
      try {
        fn(list[i], i);
      } catch (e) {
        /* one bad node must not stop the sweep */
      }
    }
  }

  function tokens(value) {
    return String(value || '')
      .split(/\s+/)
      .filter(function (t) {
        return t.length > 0;
      });
  }

  function resolves(id) {
    try {
      return !!document.getElementById(id);
    } catch (e) {
      return false;
    }
  }

  /* ---------- 1. dangling ARIA references ---------- */

  function pruneOne(el, attr) {
    var raw = el.getAttribute(attr);
    if (raw === null) return;

    var all = tokens(raw);
    var kept = all.filter(resolves);

    if (kept.length === all.length) return;

    // Remember the full list once, so a token can come back if its target does.
    var stashAttr = STASH_PREFIX + attr.replace('aria-', '');
    if (!el.hasAttribute(stashAttr)) el.setAttribute(stashAttr, all.join(' '));

    if (kept.length === 0) {
      el.removeAttribute(attr);
    } else {
      el.setAttribute(attr, kept.join(' '));
    }
  }

  function restoreOne(el, attr) {
    var stashAttr = STASH_PREFIX + attr.replace('aria-', '');
    var stashed = el.getAttribute(stashAttr);
    if (!stashed) return;

    // Only ever re-add tokens that resolve right now, so this can never
    // reintroduce a dangling reference.
    var kept = tokens(stashed).filter(resolves);
    var current = tokens(el.getAttribute(attr));

    if (kept.length === current.length && kept.join(' ') === current.join(' ')) return;

    if (kept.length === 0) {
      el.removeAttribute(attr);
    } else {
      el.setAttribute(attr, kept.join(' '));
    }
  }

  function sweepAria() {
    try {
      each(ARIA_REF_ATTRS, function (attr) {
        each(document.querySelectorAll('[' + attr + ']'), function (el) {
          pruneOne(el, attr);
        });
        each(document.querySelectorAll('[' + STASH_PREFIX + attr.replace('aria-', '') + ']'), function (el) {
          restoreOne(el, attr);
        });
      });
    } catch (e) {
      /* never throw */
    }
  }

  /* ---------- 2. keyboard access to the zoom lightbox ---------- */

  function visibleFocusables(root) {
    var out = [];
    each(root.querySelectorAll(FOCUSABLE), function (el) {
      if (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement) out.push(el);
    });
    return out;
  }

  function isOpen(lb) {
    try {
      if (lb.hasAttribute('hidden')) return false;
      if (lb.classList.contains('is-open') || lb.classList.contains('open')) return true;
      return window.getComputedStyle(lb).display !== 'none';
    } catch (e) {
      return false;
    }
  }

  function focusInto(lb) {
    var target = lb.querySelector('.pw-lb__close') || visibleFocusables(lb)[0] || lb;
    if (target === lb && !lb.hasAttribute('tabindex')) lb.setAttribute('tabindex', '-1');
    window.setTimeout(function () {
      try {
        target.focus();
      } catch (e) {}
    }, 0);
  }

  function restoreFocus() {
    var opener = lastOpener;
    lastOpener = null;
    if (!opener) return;
    window.setTimeout(function () {
      try {
        if (document.contains(opener)) opener.focus();
      } catch (e) {}
    }, 0);
  }

  function closeLightbox(lb) {
    var closer = lb.querySelector('.pw-lb__close');
    if (closer) {
      closer.click();
    } else {
      lb.classList.remove('is-open');
    }
  }

  function watchLightbox(lb) {
    if (lb.getAttribute('data-pw-a11y-lb') === '1') return;
    lb.setAttribute('data-pw-a11y-lb', '1');

    var wasOpen = isOpen(lb);

    try {
      var mo = new MutationObserver(function () {
        var nowOpen = isOpen(lb);
        if (nowOpen === wasOpen) return;
        wasOpen = nowOpen;
        if (nowOpen) {
          if (!lastOpener) lastOpener = document.activeElement;
          focusInto(lb);
        } else {
          restoreFocus();
        }
      });
      // tabindex is deliberately not in the filter, focusInto sets it.
      mo.observe(lb, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
    } catch (e) {}

    lb.addEventListener('keydown', function (ev) {
      try {
        if (ev.key === 'Escape' || ev.key === 'Esc') {
          // The section's own handler also listens for Escape. Only step in
          // if the lightbox is somehow still open a tick later.
          window.setTimeout(function () {
            if (isOpen(lb)) closeLightbox(lb);
          }, 0);
          return;
        }
        if (ev.key !== 'Tab') return;

        var items = visibleFocusables(lb);
        if (!items.length) {
          ev.preventDefault();
          return;
        }
        var first = items[0];
        var last = items[items.length - 1];

        if (ev.shiftKey && (document.activeElement === first || !lb.contains(document.activeElement))) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      } catch (e) {}
    });
  }

  function enhanceZoom(img) {
    if (img.getAttribute('data-pw-a11y-zoom') === '1') return;
    img.setAttribute('data-pw-a11y-zoom', '1');

    if (!img.hasAttribute('tabindex')) img.setAttribute('tabindex', '0');
    if (!img.hasAttribute('role')) img.setAttribute('role', 'button');
    if (!img.hasAttribute('aria-label')) {
      var alt = (img.getAttribute('alt') || '').trim();
      img.setAttribute('aria-label', alt ? 'Enlarge image: ' + alt : 'Enlarge image');
    }

    img.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
      ev.preventDefault();
      lastOpener = img;
      // Reuse the section's existing click handler rather than duplicating it.
      img.click();
    });

    img.addEventListener('click', function () {
      lastOpener = img;
    });
  }

  /* ---------- wiring ---------- */

  function scan() {
    try {
      each(document.querySelectorAll(ZOOM_SELECTOR), enhanceZoom);
      each(document.querySelectorAll(LIGHTBOX_SELECTOR), watchLightbox);
    } catch (e) {}
    sweepAria();
  }

  function start() {
    scan();

    // Facet filtering, quick add, the cart drawer and predictive search all
    // inject markup after load, so re-sweep on any structural change.
    // childList only: the attribute writes above must not retrigger this.
    try {
      var pending = null;
      var mo = new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () {
          pending = null;
          scan();
        }, 150);
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();